import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import ScrollToTop from "@/components/base/ScrollToTop";
import SchemaOrg from "@/components/base/SchemaOrg";
import { movies, categories } from "@/mocks/movies";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const movie = useMemo(() => movies.find((m) => m.slug === slug), [slug]);
  const catInfo = useMemo(
    () => (movie ? categories.find((c) => c.slug === movie.category) : null),
    [movie]
  );

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies.filter((m) => m.category === movie.category && m.id !== movie.id).slice(0, 4);
  }, [movie]);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您访问的影片不存在或已下架</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${movie.title} - 八戒影院高清免费在线观看`;

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Movie"
        data={{
          name: movie.title,
          description: movie.description,
          image: movie.poster,
          dateCreated: `${movie.year}`,
          director: { "@type": "Person", name: movie.director },
          actor: movie.cast.map((name) => ({ "@type": "Person", name })),
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: movie.rating,
            bestRating: "10",
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://www.bajiecinema.com/" },
            { "@type": "ListItem", position: 2, name: catInfo?.name || "影片", item: `https://www.bajiecinema.com/category/${movie.category}` },
            { "@type": "ListItem", position: 3, name: movie.title },
          ],
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: pageTitle,
          description: movie.description,
          url: `https://www.bajiecinema.com/article/${movie.slug}`,
        }}
      />
      <Navbar />
      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: catInfo?.name || "影片", href: `/category/${movie.category}` },
            { label: movie.title },
          ]}
        />

        <article className="px-4 md:px-6 pb-10 md:pb-14">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[320px] flex-shrink-0">
                <div className="rounded-lg overflow-hidden aspect-[2/3] bg-background-100">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    title={`${movie.title} - 八戒影院高清免费在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="flex items-center gap-1 bg-primary-100/80 text-primary-700 text-sm font-semibold px-2.5 py-1 rounded-md">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-xs"></i>
                    </div>
                    {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-600">{movie.year}</span>
                  <span className="text-sm text-foreground-600">{movie.duration}</span>
                  <span className="text-sm bg-background-100 text-foreground-600 px-2.5 py-1 rounded-md">
                    {catInfo?.name}
                  </span>
                </div>

                <div className="flex flex-col gap-3 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 w-16 flex-shrink-0">导演</span>
                    <span className="text-sm text-foreground-800">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 w-16 flex-shrink-0">主演</span>
                    <span className="text-sm text-foreground-800">{movie.cast.join(" / ")}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 w-16 flex-shrink-0">类型</span>
                    <span className="text-sm text-foreground-800">{catInfo?.name}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-accent-100/80 text-accent-700 px-2.5 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="prose prose-sm max-w-none">
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">剧情简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <button className="px-8 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    立即播放
                  </button>
                  <button className="px-8 py-3 border border-background-200 text-foreground-700 font-medium text-sm rounded-md hover:border-primary-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                    加入收藏
                  </button>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-12 md:mt-14">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard
                      key={m.id}
                      id={m.id}
                      title={m.title}
                      slug={m.slug}
                      category={m.category}
                      year={m.year}
                      rating={m.rating}
                      poster={m.poster}
                      tags={m.tags}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}